// Uzbek Cyrillic -> Latin transliteration. Safari's Intl has date & relative-time
// data for uz-Cyrl but NOT uz-Latn (which falls back to English / "M01"), so for the
// Latin UI we format the date in Cyrillic and transliterate the result to Latin.
const UZ_MAP = {'а':'a','б':'b','в':'v','г':'g','д':'d','ж':'j','з':'z','и':'i','й':'y',
  'к':'k','л':'l','м':'m','н':'n','о':'o','п':'p','р':'r','с':'s','т':'t','у':'u','ф':'f',
  'х':'x','ц':'ts','ч':'ch','ш':'sh','ъ':"'",'ь':'','ы':'i','э':'e','қ':'q','ғ':"g'",
  'ҳ':'h','ў':"o'",'ё':'yo','ю':'yu','я':'ya'};
function uzCyrlToLatn(s) {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i], lo = ch.toLowerCase(), up = ch !== lo;
    let r;
    if (lo === 'е') {
      const p = s[i - 1] || '';
      r = (!/[а-яёўғҳқ]/i.test(p) || /[аоуэиўёюя]/i.test(p)) ? 'ye' : 'e';
    } else if (Object.prototype.hasOwnProperty.call(UZ_MAP, lo)) {
      r = UZ_MAP[lo];
    } else {
      out += ch; continue;
    }
    out += (up && r) ? r[0].toUpperCase() + r.slice(1) : r;
  }
  return out;
}
function resolveLocale(locale) {
  return locale || (typeof document !== 'undefined' && document.documentElement.lang) ||
    window.navigator.userLanguage || window.navigator.language;
}
const isUzLatn = locale => /^uz-latn/i.test(locale);
// Locale to hand to Intl (uz-Latn -> uz-Cyrl) plus the matching post-processor.
const intlLocale = locale => isUzLatn(locale) ? 'uz-Cyrl' : locale;
const uzPost = (s, locale) => isUzLatn(locale) ? uzCyrlToLatn(s) : s;

// Short representation of time in the past.
export function shortDateFormat(then, locale) {
  locale = resolveLocale(locale);
  const il = intlLocale(locale);
  const now = new Date();
  if (then.getFullYear() == now.getFullYear()) {
    // Same year.
    if (then.getMonth() == now.getMonth() && then.getDate() == now.getDate()) {
      // Same month and day, show time only.
      return uzPost(then.toLocaleTimeString(il, {hour12: false, hour: '2-digit', minute: '2-digit'}), locale);
    }
    // Different month and/or day, show month day, time.
    return uzPost(then.toLocaleDateString(il,
      {hour12: false, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}), locale);
  }
  // Different year: just show the date.
  return uzPost(then.toLocaleDateString(il, {year: 'numeric', month: 'short', day: 'numeric'}), locale);
}

// Representation a date relative to now.
export function relativeDateFormat(then, locale) {
  locale = resolveLocale(locale);
  const il = intlLocale(locale);
  const now = new Date();
  const thenDays = Math.floor((then.getTime() - then.getTimezoneOffset() * 60_000) / 86_400_000);
  const nowDays = Math.floor((now.getTime() - now.getTimezoneOffset() * 60_000) / 86_400_000);
  const diff = thenDays - nowDays;
  if (Math.abs(diff) < 2) {
    // Today or yesterday.
    return uzPost(new Intl.RelativeTimeFormat(il, { numeric: 'auto' }).format(diff, 'day'), locale);
  }
  // More than two days: just show the date.
  return uzPost(new Intl.DateTimeFormat(il).format(then), locale);
}

// Convert seconds to [hours:]minutes:seconds, i.e. 156 sec -> 2:36, 3756 sec -> 1:02:36.
// If <code>fixedMins</code> is true, then minutes are represented by at least two digits.
export function secondsToTime(seconds, fixedMin) {
  if (typeof seconds != 'number') {
    return '';
  }

  let min = (Math.floor(seconds / 60) | 0) % 60;
  let hours = Math.floor(seconds / 3600) | 0;

  if (fixedMin || hours > 0) {
    min = min < 10 ? `0${min}` : min;
  }
  let sec = (seconds | 0) % 60;
  sec = sec < 10 ? `0${sec}` : sec;
  if (hours == 0) {
    return `${min}:${sec}`;
  }
  return `${hours}:${min}:${sec}`;
}

// Convert a number of bytes to human-readable format.
export function bytesToHumanSize(bytes) {
  if (!bytes || bytes == 0) {
    return '0 Bytes';
  }

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const bucket = Math.min(Math.floor(Math.log2(bytes) / 10) | 0, sizes.length-1);
  const count = bytes / Math.pow(1024, bucket);
  const round = bucket > 0 ? (count < 3 ? 2 : (count < 30 ? 1 : 0)) : 0;
  return count.toFixed(round) + ' ' + sizes[bucket];
}

// Shorten a file name to be under maxLength by clipping out the middle.
export function shortenFileName(filename, maxLength) {
  if (typeof filename != 'string') {
    return filename;
  }
  return filename.length > maxLength ?
    filename.slice(0, maxLength/2 - 1) + '…' + filename.slice(1 - maxLength/2) : filename;
}

// Get 32 bit integer hash value for a string. Ideally it should produce the same value
// as Java's String#hash().
function stringToColorHash(value) {
  let hash = 0;
  value = '' + value;
  for (let i = 0; i < value.length; i++) {
    hash = ((hash << 5) - hash) + value.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash;
}

// User avatar letter color id.
export function letterTileColorId(userId) {
  return Math.abs(stringToColorHash(userId)) % 16;
}

// Converts user or topic ID to a CSS color class. Ideally it should produce the same color value as Java version.
// params:
// - {string} id: user or topic ID
// - {boolean} light: light or dark version of the color.
// - {boolean} fg: foreground (text) or background color.
export function idToColorClass(id, light, fg) {
  return (light ? 'lt-' : 'dk-') + (fg ? 'fg-' : 'bg-') + letterTileColorId(id);
}

// Takes ISO Alpha-2 country code, like 'US' and converts it to a flag emoji.
export function flagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 0x1F1A5 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

// Truncate a string to a specific length and add ellipsis if needed.
// Support both LTR and RTL languages.
export function truncateString(str, maxLength) {
  if (typeof str !== 'string') {
    return str;
  }
  if (str.length <= maxLength) {
    return str;
  }

  // Detect RTL characters in the string and truncate accordingly.
  const isRTL = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(str);
  return isRTL ? '…' + str.slice(0, maxLength - 1) : str.slice(0, maxLength - 1) + '…';
}
